'use client';

import { useState, useEffect, useRef, useCallback, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import { useRouter, usePathname } from 'next/navigation';
import { X, Minimize2, Maximize2, Loader2, Send, PictureInPicture2 } from "@/lib/icons";
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useChatStore } from '@/lib/goonpals-chat-store';
import { useFloatingChatStore } from '@/lib/floating-chat-store';
import type { ChatMessage } from '@/lib/goonpals-types';
import { useTranslations } from "next-intl"

// Stable reference for the "no session yet" case — a fresh `[]` literal
// returned from a zustand selector on every render breaks the
// useSyncExternalStore reference-equality check and causes an infinite
// render loop ("Maximum update depth exceeded").
const EMPTY_MESSAGES: ChatMessage[] = [];

const BUBBLE_SIZE = 64;
// Gap kept between the bubble and the true screen edge, both while dragging
// and once snapped — it should sit near the edge, never flush against it.
const EDGE_MARGIN = 30;
const POSITION_STORAGE_KEY = 'goonru:companion-widget-position';
const DRAG_THRESHOLD_PX = 4;

type WidgetPosition = { x: number; y: number };

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function bounds() {
  return {
    minX: EDGE_MARGIN,
    maxX: window.innerWidth - EDGE_MARGIN - BUBBLE_SIZE,
    minY: EDGE_MARGIN,
    maxY: window.innerHeight - EDGE_MARGIN - BUBBLE_SIZE,
  };
}

function defaultPosition(): WidgetPosition {
  const { maxX, maxY } = bounds();
  return { x: maxX, y: maxY };
}

/** Pins whichever axis is closest to a viewport edge to that edge (with the
 * EDGE_MARGIN gap kept) — the "glued to the edge" behavior the widget always
 * snaps back to on release, never left floating loose in the middle of the
 * page. */
function snapToNearestEdge(pos: WidgetPosition): WidgetPosition {
  const { minX, maxX, minY, maxY } = bounds();
  const x = clamp(pos.x, minX, maxX);
  const y = clamp(pos.y, minY, maxY);
  const candidates: Array<{ edge: 'left' | 'right' | 'top' | 'bottom'; distance: number }> = [
    { edge: 'left', distance: x - minX },
    { edge: 'right', distance: maxX - x },
    { edge: 'top', distance: y - minY },
    { edge: 'bottom', distance: maxY - y },
  ];
  const nearest = candidates.reduce((a, b) => (b.distance < a.distance ? b : a)).edge;
  if (nearest === 'left') return { x: minX, y };
  if (nearest === 'right') return { x: maxX, y };
  if (nearest === 'top') return { x, y: minY };
  return { x, y: maxY };
}

// This widget renders nothing until a chat screen explicitly "takes the
// chat with you" (see the PictureInPicture2 button in ChatToolbar), which
// pins a companion + session into useFloatingChatStore. It never auto-shows
// on its own — closing it fully clears that pinned state.
export function GlobalCompanionWidget() {
  const t = useTranslations("globalCompanionWidget")
  const router = useRouter();
  const pathname = usePathname();
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadedFor = useRef<string | null>(null);

  const companion = useFloatingChatStore((s) => s.companion);
  const sessionId = useFloatingChatStore((s) => s.sessionId);
  const minimized = useFloatingChatStore((s) => s.minimized);
  const setMinimized = useFloatingChatStore((s) => s.setMinimized);
  const deactivate = useFloatingChatStore((s) => s.deactivate);

  const sendMessage = useChatStore((s) => s.sendMessage);
  const fetchMessages = useChatStore((s) => s.fetchMessages);
  const messages = useChatStore((s) => (sessionId ? s.messages[sessionId] ?? EMPTY_MESSAGES : EMPTY_MESSAGES));

  // Document Picture-in-Picture: an actual OS-level floating window (not
  // just fixed-positioned in-page) that survives switching tabs/apps —
  // Chrome/Edge desktop only. The panel below renders identically whether
  // it's sitting inline in the page or portaled into this window; only the
  // container changes. Closing the PiP window (however the user does it —
  // its own close button, alt-F4, etc.) fires 'pagehide', which is the only
  // reliable signal to fall back to the inline panel.
  const [pipSupported, setPipSupported] = useState(false);
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  useEffect(() => {
    setPipSupported(typeof window !== 'undefined' && 'documentPictureInPicture' in window);
  }, []);

  const openPip = useCallback(async () => {
    const dpip = (window as any).documentPictureInPicture;
    if (!dpip) return;
    const win: Window = await dpip.requestWindow({ width: 340, height: 460 });

    // The PiP window is a genuinely separate document with an empty head —
    // copy every stylesheet across so Tailwind/theme classes still resolve.
    // Cross-origin sheets throw on .cssRules access; skip those (there are
    // none in this app's own bundle, only a defensive guard).
    [...document.styleSheets].forEach((sheet) => {
      try {
        const css = [...sheet.cssRules].map((r) => r.cssText).join('');
        const style = win.document.createElement('style');
        style.textContent = css;
        win.document.head.appendChild(style);
      } catch {
        /* cross-origin stylesheet — skip */
      }
    });
    // Carries dark/light mode and any other root-level theme classes across,
    // since Tailwind's dark: variant keys off a class on <html>, not <body>.
    win.document.documentElement.className = document.documentElement.className;
    win.document.body.className = 'bg-background text-foreground';

    win.addEventListener('pagehide', () => setPipWindow(null), { once: true });
    setPipWindow(win);
  }, []);

  const closePip = useCallback(() => {
    pipWindow?.close();
    setPipWindow(null);
  }, [pipWindow]);

  // Drag/snap position — null until mounted client-side (window isn't
  // available during SSR); renders with the original static bottom-4/
  // right-4 classes until this loads, then switches to inline positioning.
  const [position, setPosition] = useState<WidgetPosition | null>(null);
  const [dragPos, setDragPos] = useState<WidgetPosition | null>(null);
  const isDragging = dragPos !== null;
  const dragStart = useRef<{ pointerX: number; pointerY: number; originX: number; originY: number; moved: boolean } | null>(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(POSITION_STORAGE_KEY);
      setPosition(saved ? JSON.parse(saved) : defaultPosition());
    } catch {
      setPosition(defaultPosition());
    }
  }, []);

  const onSameChatPage = !!companion && pathname === `/goonpals/chat/${companion.id}`;

  useEffect(() => {
    if (!minimized && sessionId && loadedFor.current !== sessionId) {
      loadedFor.current = sessionId;
      setSessionReady(false);
      fetchMessages(sessionId).finally(() => setSessionReady(true));
    }
  }, [minimized, sessionId, fetchMessages]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length]);

  const handlePointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!position) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStart.current = { pointerX: e.clientX, pointerY: e.clientY, originX: position.x, originY: position.y, moved: false };
    setDragPos(position);
  }, [position]);

  const handlePointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const start = dragStart.current;
    if (!start) return;
    const dx = e.clientX - start.pointerX;
    const dy = e.clientY - start.pointerY;
    if (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX) start.moved = true;
    const { minX, maxX, minY, maxY } = bounds();
    setDragPos({
      x: clamp(start.originX + dx, minX, maxX),
      y: clamp(start.originY + dy, minY, maxY),
    });
  }, []);

  const handlePointerUp = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const start = dragStart.current;
    dragStart.current = null;
    if (!start) return;
    if (!start.moved) {
      // A plain tap, not a drag — restore the chat instead of snapping.
      setDragPos(null);
      setMinimized(false);
      return;
    }
    const snapped = snapToNearestEdge(dragPos ?? { x: start.originX, y: start.originY });
    setPosition(snapped);
    setDragPos(null);
    try {
      localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(snapped));
    } catch {
      // Best-effort — losing a saved position just means it resets to the default corner next load.
    }
  }, [dragPos, setMinimized]);

  const handleOpenFullChat = () => {
    if (companion) {
      router.push(`/goonpals/chat/${companion.id}`);
    }
  };

  const handleSend = async () => {
    const content = input.trim();
    if (!content || !sessionId || sending) return;
    setSending(true);
    setInput('');
    try {
      await sendMessage(sessionId, { content, messageType: 'text' });
    } finally {
      setSending(false);
    }
  };

  if (!companion || !sessionId || onSameChatPage) {
    return null;
  }

  const activePos = dragPos ?? position;
  // Which side of the screen the bubble is currently pinned to — decides
  // which direction the expanded panel opens, so it never renders
  // off-screen (e.g. pinned to the top edge means the panel has to grow
  // downward, not upward like the original bottom-right default did).
  const nearRight = activePos ? activePos.x > window.innerWidth - activePos.x - BUBBLE_SIZE : true;
  const nearBottom = activePos ? activePos.y > window.innerHeight - activePos.y - BUBBLE_SIZE : true;

  const bubbleStyle: CSSProperties = activePos
    ? { left: activePos.x, top: activePos.y, right: 'auto', bottom: 'auto', touchAction: 'none' }
    : {};

  if (minimized) {
    return (
      <div
        className={`fixed z-50 ${activePos ? '' : 'bottom-4 right-4'} ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        style={bubbleStyle}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <Button
          size="lg"
          className="rounded-full w-16 h-16 p-0 shadow-lg"
          title={`Continue chatting with ${companion.name} — drag to move`}
        >
          <div className="relative w-full h-full">
            <Image
              src={companion.avatarUrl || '/default-avatar.png'}
              alt={companion.name}
              fill
              className="rounded-full object-cover pointer-events-none"
              style={{ objectPosition: `${companion.avatarFocalX ?? 50}% ${companion.avatarFocalY ?? 50}%` }}
            />
          </div>
        </Button>
      </div>
    );
  }

  const panelStyle: CSSProperties = activePos
    ? {
        [nearRight ? 'right' : 'left']: nearRight ? window.innerWidth - activePos.x - BUBBLE_SIZE : activePos.x,
        [nearBottom ? 'bottom' : 'top']: nearBottom ? window.innerHeight - activePos.y - BUBBLE_SIZE : activePos.y,
      }
    : {};

  // Identical whether it ends up inline (a fixed-position Card in this
  // page) or portaled into the PiP window's own document — only the
  // container differs, so there's exactly one version of this UI to keep
  // in sync with the real chat instead of two.
  const panelContent = (
    <div className="p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="relative w-10 h-10 rounded-full overflow-hidden">
            <Image
              src={companion.avatarUrl || '/default-avatar.png'}
              alt={companion.name}
              fill
              className="object-cover"
              style={{ objectPosition: `${companion.avatarFocalX ?? 50}% ${companion.avatarFocalY ?? 50}%` }}
            />
          </div>
          <div>
            <h3 className="font-semibold text-sm">{companion.name}</h3>
            <p className="text-xs text-muted-foreground">{pipWindow ? 'Floating window' : 'Following you'}</p>
          </div>
        </div>
        <div className="flex gap-1">
          {pipWindow ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={closePip}
              className="h-8 w-8"
              title={t("returnToInPageBubble")}
            >
              <PictureInPicture2 className="w-4 h-4" />
            </Button>
          ) : (
            pipSupported && (
              <Button
                variant="ghost"
                size="icon"
                onClick={openPip}
                className="h-8 w-8"
                title={t("popOutIntoAFloatingWindowStaysOnTopEv")}
              >
                <PictureInPicture2 className="w-4 h-4" />
              </Button>
            )
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleOpenFullChat}
            className="h-8 w-8"
            title={t("openFullChat")}
          >
            <Maximize2 className="w-4 h-4" />
          </Button>
          {!pipWindow && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMinimized(true)}
              className="h-8 w-8"
              title={t("minimize")}
            >
              <Minimize2 className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => { closePip(); deactivate(); }}
            className="h-8 w-8"
            title={t("stopFollowingDismissThisChat")}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Inline message thread */}
      <div ref={scrollRef} className="max-h-60 space-y-2 overflow-y-auto rounded-lg bg-muted/50 p-2">
        {!sessionReady ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <p className="p-2 text-sm text-muted-foreground">
            {companion.introduction || `Hey! I'm ${companion.name}. Say hi!`}
          </p>
        ) : (
          messages.slice(-8).map((m) => (
            <div key={m.id} className={`flex ${m.senderType === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-xl px-3 py-1.5 text-xs whitespace-pre-wrap break-words ${
                  m.senderType === 'user'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-200 text-gray-900 dark:bg-gray-800 dark:text-gray-100'
                }`}
              >
                {m.content}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Inline input */}
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={`Message ${companion.name}...`}
          disabled={!sessionReady || sending}
          className="h-9 text-sm"
        />
        <Button
          onClick={handleSend}
          disabled={!sessionReady || sending || !input.trim()}
          size="icon"
          className="h-9 w-9 flex-shrink-0"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );

  if (pipWindow) {
    return createPortal(panelContent, pipWindow.document.body);
  }

  return (
    <Card className={`fixed z-50 w-80 shadow-2xl ${activePos ? '' : 'bottom-4 right-4'}`} style={panelStyle}>
      {panelContent}
    </Card>
  );
}
